/**
 * One dock, two occupants.
 *
 * Implements the "Lodgeic AI Assistant - Floating" design turn 2: Intercom moves
 * INSIDE a Lodgeic-owned dock rather than sitting beside it. This component owns
 * only the collapsed presentation — the AI bubble is passed in as `children` so
 * AssistantLauncher keeps every bit of its own state.
 *
 * Two presentations from the design, both supported:
 *   'hover' (2a) — only the orange bubble at rest; hover/focus grows the stack
 *                  upward at 150ms. Quietest corner, support one item down.
 *   'pill'  (2b) — a white pill holding support above a hairline divider with
 *                  the AI at the bottom. Nothing hidden, more permanent pixels.
 *
 * Touch devices always get 'pill': 2a is reachable only by hover or focus, and
 * on a touchscreen the single tap that would expand the stack is the same tap
 * that opens the AI. Support would be unreachable.
 *
 * Order is fixed in both: support sits ABOVE the AI, never below it, so the
 * primary action stays closest to the corner.
 */

import React, { useEffect, useState } from 'react';
import {
  ensureIntercomBooted,
  showIntercom,
  subscribeMessengerVisibility,
  subscribeUnreadCount,
} from '../../utils/intercom';
import './AssistantDock.css';

/**
 * Design variant. 'hover' is 2a, 'pill' is 2b — flip this one constant to switch
 * the whole app. Touch always renders 'pill' regardless (see above).
 */
export const DOCK_VARIANT = 'hover';

/**
 * "What's new" is drawn in the design's 2a but is off until Intercom News is
 * actually configured for the workspace — showSpace('news') on a workspace
 * without News opens an empty panel, which is worse than not offering it.
 */
export const SHOW_WHATS_NEW = false;

const SupportIcon = ({ size = 19 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.5 8.5 0 018 8v.5z" />
  </svg>
);

const WhatsNewIcon = ({ size = 19 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 8a6 6 0 00-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9z" />
    <path d="M10.5 21a2 2 0 003 0" />
  </svg>
);

function prefersTouch() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(hover: none), (pointer: coarse)').matches;
}

const AssistantDock = ({ children, corner = 'bottom-right', aiBusy = false }) => {
  const [variant, setVariant] = useState(() => (prefersTouch() ? 'pill' : DOCK_VARIANT));
  const [supportReady, setSupportReady] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messengerOpen, setMessengerOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Re-evaluate on input-capability change (a tablet gaining a keyboard/mouse,
  // or the browser being resized across the touch breakpoint).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(hover: none), (pointer: coarse)');
    const apply = () => setVariant(query.matches ? 'pill' : DOCK_VARIANT);
    apply();
    query.addEventListener?.('change', apply);
    return () => query.removeEventListener?.('change', apply);
  }, []);

  useEffect(() => {
    let unsubscribeUnread = () => {};
    let unsubscribeVisibility = () => {};
    let cancelled = false;
    (async () => {
      const ready = await ensureIntercomBooted();
      if (cancelled) return;
      setSupportReady(ready);
      if (!ready) return;
      unsubscribeUnread = subscribeUnreadCount(setUnread);
      unsubscribeVisibility = subscribeMessengerVisibility(setMessengerOpen);
    })();
    return () => {
      cancelled = true;
      unsubscribeUnread();
      unsubscribeVisibility();
    };
  }, []);

  const openSupport = () => {
    if (!showIntercom()) return;
    setExpanded(false);
  };

  const openWhatsNew = () => {
    if (typeof window?.Intercom !== 'function') return;
    window.Intercom('showSpace', 'news');
    setExpanded(false);
  };

  // With no messenger there is nothing to dock, so the AI bubble stands alone
  // exactly as it did before — never a dock chrome wrapped around one item.
  if (!supportReady) {
    return <div className="ad-root" data-variant="solo">{children}</div>;
  }

  // The mirror case: support with no assistant. The AI is feature-flagged and
  // Intercom's own launcher is hidden, so without this the corner would be EMPTY
  // for a customer without the AI — the flag would silently take their support
  // channel with it. Support becomes the corner's primary control instead, which
  // is what it was before the dock existed.
  if (!children) {
    return (
      <div className="ad-root" data-variant="support-only" data-corner={corner}>
        <button
          type="button"
          className={`ad-support-primary ${messengerOpen ? 'is-active' : ''}`}
          onClick={openSupport}
          aria-label={unread > 0 ? `Support · ${unread} unread` : 'Support'}
        >
          <SupportIcon size={24} />
          {unread > 0 && <span className="ad-primary-unread" aria-hidden="true" />}
        </button>
      </div>
    );
  }

  const secondaryItems = [
    ...(SHOW_WHATS_NEW ? [{
      key: 'whats-new',
      label: "What's new",
      icon: <WhatsNewIcon />,
      onClick: openWhatsNew,
    }] : []),
    {
      key: 'support',
      label: unread > 0 ? `Support · ${unread} unread` : 'Support',
      icon: <SupportIcon />,
      onClick: openSupport,
      unread,
      active: messengerOpen,
    },
  ];

  // Rendered bottom-to-top by column-reverse, so the array order below is
  // "closest to the corner first" and support ends up on top.
  return (
    <div
      className="ad-root"
      data-variant={variant}
      data-corner={corner}
      data-expanded={variant === 'hover' && expanded ? 'true' : 'false'}
      onMouseEnter={variant === 'hover' ? () => setExpanded(true) : undefined}
      onMouseLeave={variant === 'hover' ? () => setExpanded(false) : undefined}
      onFocus={variant === 'hover' ? () => setExpanded(true) : undefined}
      onBlur={variant === 'hover' ? (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setExpanded(false);
      } : undefined}
    >
      <div className="ad-stack">
        <div className="ad-primary">
          {children}
          {/* In 2a the collapsed bubble is the only visible surface, so an unread
              reply has to show there. It sits bottom-left in support red because
              the AI's own "items ready" count already owns the top-right corner
              of that bubble — the design did not have to reconcile the two. */}
          {variant === 'hover' && !expanded && unread > 0 && (
            <span className="ad-primary-unread" aria-hidden="true" />
          )}
        </div>

        {variant === 'pill' && <span className="ad-divider" aria-hidden="true" />}

        {secondaryItems.map(item => (
          <div className="ad-item" key={item.key}>
            <button
              type="button"
              className={`ad-item-btn ${item.active ? 'is-active' : ''}`}
              onClick={item.onClick}
              aria-label={item.label}
              tabIndex={variant === 'hover' && !expanded ? -1 : 0}
            >
              {item.icon}
              {item.unread > 0 && <span className="ad-item-unread" aria-hidden="true" />}
            </button>
            {variant === 'hover' && <span className="ad-item-label" aria-hidden="true">{item.label}</span>}
          </div>
        ))}
      </div>
      {aiBusy && <span className="ad-sr-only" role="status">Lodgeic AI is working</span>}
    </div>
  );
};

export default AssistantDock;
